/**
 * LOINC code shape + check-digit validation — the gate that decides whether a source-provided code
 * is trustworthy enough to key identity on.
 *
 * Exactly ONE implementation, and both consumers reach this file: the ledger identity policy
 * deep-imports `@vibrant/models/fhir/loinc-code` (`packages/db/src/ledger/identity/policies/lab.ts`
 * — the deep path rather than the barrel, per the note in `write/lab-schemas.ts`), and the app's
 * parser/persistence path re-exports it from `apps/web/lib/loinc/loinc-code.ts`. Never fork it: if
 * the two ever disagreed about whether a code is canonical, identity would key on the LOINC while
 * the parser keyed on the name, and one analyte would split into two clinical facts.
 *
 * It lives in `@vibrant/models` because it is pure and client-safe — no imports at all, so neither
 * consumer takes on the other's dependencies and the mobile bundle stays clean.
 */

/**
 * Shape of a LOINC code: a 1-8 digit number part, a hyphen, then a single check digit (e.g.
 * `718-7`). The 8-digit upper bound matches the longest valid LOINC numbers; correctness is still
 * enforced by the check digit in {@link isCanonicalLoincCode}.
 */
export const LOINC_CODE_PATTERN = /^\d{1,8}-\d$/;

/**
 * Checks whether a source-provided LOINC is canonical enough to persist (valid shape + check digit).
 */
export function isCanonicalLoincCode(code: string | null | undefined): code is string {
  if (!code || !LOINC_CODE_PATTERN.test(code)) {
    return false;
  }

  const [baseCode, checkDigit] = code.split("-");
  if (!baseCode || !checkDigit) {
    return false;
  }

  return calculateLoincCheckDigit(baseCode) === Number(checkDigit);
}

function calculateLoincCheckDigit(baseCode: string): number {
  let sum = 0;
  let shouldDouble = true;

  for (let index = baseCode.length - 1; index >= 0; index -= 1) {
    const digit = Number(baseCode[index]);
    const value = shouldDouble ? digit * 2 : digit;
    sum += value > 9 ? value - 9 : value;
    shouldDouble = !shouldDouble;
  }

  return (10 - (sum % 10)) % 10;
}
